// =============================================================
// App root — provider stack + auth-gated routing.
//
//   PrefsProvider (theme + lang)
//     ChromeProvider (toasts + confirm)
//       QueryClientProvider (TanStack Query over /api/v1)
//         AuthProvider (login → MFA → session + RBAC)
//           Router → Login (anonymous/mfa) | Shell + routed flows (authed)
//
// `api` may be injected (tests bind an ApiClient to the in-process Hono app).
// =============================================================

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { MemoryRouter, Navigate, Route, Routes, type MemoryRouterProps } from "react-router-dom";
import { BrowserRouter } from "react-router-dom";
import type { ApiClient } from "@perpetuum/core";
import { PrefsProvider } from "./PrefsContext.js";
import { ChromeProvider } from "./ChromeContext.js";
import { SyncProvider } from "./SyncContext.js";
import { AuthProvider, useAuth } from "./AuthContext.js";
import { Shell } from "./Shell.js";
import { ChromeHost } from "../components/ChromeHost.js";
import { Login } from "../flows/Login.js";
import { Dashboard } from "../flows/Dashboard.js";
import { Tree } from "../flows/Tree.js";
import { WorkBoard } from "../flows/WorkBoard.js";
import { TasksRegister } from "../flows/TasksRegister.js";
import { Scheduler } from "../flows/Scheduler.js";
import { Inventory } from "../flows/Inventory.js";
import { Downtimes } from "../flows/Downtimes.js";
import { MeasurementTemplates } from "../flows/MeasurementTemplates.js";
import { Meters } from "../flows/Meters.js";
import { Reports } from "../flows/Reports.js";
import { Settings } from "../flows/Settings.js";
import { Access } from "../flows/Access.js";
import { Executors } from "../flows/Executors.js";
import { Groups } from "../flows/Groups.js";
import { Admin } from "../flows/Admin.js";
import { Review } from "../flows/Review.js";
import { useScopedT } from "../lib/i18n-keys.js";

function Routed() {
  const { phase } = useAuth();
  const t = useScopedT();

  if (phase === "loading") {
    return <div className="loading-screen">{t("common.loading")}</div>;
  }
  if (phase !== "authenticated") {
    return <Login />;
  }
  return (
    <Routes>
      <Route element={<Shell />}>
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/tree" element={<Tree />} />
        <Route path="/workboard" element={<WorkBoard />} />
        <Route path="/tasks-register" element={<TasksRegister />} />
        <Route path="/scheduler" element={<Scheduler />} />
        <Route path="/inventory" element={<Inventory />} />
        <Route path="/downtimes" element={<Downtimes />} />
        <Route path="/measurement-templates" element={<MeasurementTemplates />} />
        <Route path="/meters" element={<Meters />} />
        <Route path="/reports" element={<Reports />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/access" element={<Access />} />
        <Route path="/executors" element={<Executors />} />
        <Route path="/groups" element={<Groups />} />
        <Route path="/admin" element={<Admin />} />
        <Route path="/review" element={<Review />} />
        <Route path="*" element={<Navigate to="/tree" replace />} />
      </Route>
    </Routes>
  );
}

export interface AppProps {
  /** Inject a pre-built ApiClient (tests). Defaults to the same-origin client. */
  api?: ApiClient;
  /** Use MemoryRouter (tests) instead of BrowserRouter. */
  router?: "browser" | "memory";
  /** Initial entries for the MemoryRouter (tests). */
  initialEntries?: MemoryRouterProps["initialEntries"];
}

export function App({ api, router = "browser", initialEntries }: AppProps) {
  const queryClient = new QueryClient({
    defaultOptions: { queries: { retry: false, refetchOnWindowFocus: false } },
  });
  const Router = router === "memory" ? MemoryRouter : BrowserRouter;
  const routerProps = router === "memory" ? { initialEntries } : {};

  return (
    <PrefsProvider>
      <ChromeProvider>
        <SyncProvider>
          <QueryClientProvider client={queryClient}>
            <AuthProvider api={api}>
              <Router {...routerProps}>
                <Routed />
              </Router>
              <ChromeHost />
            </AuthProvider>
          </QueryClientProvider>
        </SyncProvider>
      </ChromeProvider>
    </PrefsProvider>
  );
}
